import { useMemo, useState, useEffect, type FC, type ReactNode } from "react";
import "../styles/AddToFavorites.css";
import { MdCloudUpload, MdWarning, MdChevronRight, MdExpandMore, MdFolder, MdFolderOpen, MdCreateNewFolder, MdClose } from "react-icons/md";
import type { Project, FolderItem } from "../../../../common/src/types/types";
import { getProjects, addUserFavorite } from "../../apiClient";
import { getOneDriveFolders, convertToFolderNodes, createOneDriveFolder } from "../../graphClient";
import { useAppContext } from "../contexts/AppContext";

interface FolderNode {
    id: string;
    name: string;
    path?: string;
    children?: FolderNode[];
}

const AddToFavorites: FC = () => {
    const { navigateToFolders, handleAddToFavorites: onAddToFavorites, favorites: currentFavorites, refreshFavorites } = useAppContext();

    const [viewMode, setViewMode] = useState<"favorites" | "onedrive">("favorites");
    const [oneDriveMode, setOneDriveMode] = useState<"existing" | "new">("existing");
    const [selectedOneDriveFolder, setSelectedOneDriveFolder] = useState<string | null>(null);
    const [newFolderName, setNewFolderName] = useState("");
    const [newFolderParent, setNewFolderParent] = useState("root");
    const [expandedFolders, setExpandedFolders] = useState<Set<string>>(new Set(["root"]));
    const [oneDriveFolders, setOneDriveFolders] = useState<FolderNode[]>([]);
    const [isOneDriveLoading, setIsOneDriveLoading] = useState(false);
    const [oneDriveError, setOneDriveError] = useState<string | null>(null);
    const [oneDriveAvailable, setOneDriveAvailable] = useState(true);
    const [loadedFolders, setLoadedFolders] = useState<Set<string>>(new Set());
    const [oneDriveFolderState, setOneDriveFolderState] = useState<"idle" | "creating" | "created" | "existing">("idle");

    const [allProjects, setAllProjects] = useState<Project[]>([]);
    const [searchResults, setSearchResults] = useState<Project[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isSearchLoading, setIsSearchLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [searchTerm, setSearchTerm] = useState("");
    const [addedProjects, setAddedProjects] = useState<Set<string>>(new Set());


    useEffect(() => {
        let isActive = true;
        const loadProjects = async () => {
            try {
                setIsLoading(true);
                setError(null);
                const projects = await getProjects();
                if (isActive) {
                    setAllProjects(projects);
                }
            } catch (err) {
                if (isActive) {
                    setError("Failed to load projects. Please try again.");
                    console.error("Error loading projects:", err);
                }
            } finally {
                if (isActive) {
                    setIsLoading(false);
                }
            }
        };

        loadProjects();
        return () => {
            isActive = false;
        };
    }, []);
    // Initialize OneDrive and load root folders
    useEffect(() => {
        const initializeOneDrive = async () => {
            try {
                setIsOneDriveLoading(true);
                setOneDriveError(null);

                const rootFolders = await getOneDriveFolders();
                const folderNodes = convertToFolderNodes(rootFolders, 'OneDrive');

                // Add root node
                const rootNode: FolderNode = {
                    id: 'root',
                    name: 'OneDrive',
                    path: 'OneDrive',
                    children: folderNodes
                };

                setOneDriveFolders([rootNode]);
                setExpandedFolders(prev => {
                    const next = new Set(prev);
                    next.add('root');
                    return next;
                });
                setLoadedFolders(prev => {
                    const next = new Set(prev);
                    next.add('root');
                    return next;
                });
            } catch (err) {
                console.error('Failed to initialize OneDrive:', err);
                setOneDriveError('Failed to load OneDrive folders');
                setOneDriveAvailable(false);
            } finally {
                setIsOneDriveLoading(false);
            }
        };

        initializeOneDrive();
    }, []);

    // Load child folders on demand
    const loadChildFolders = async (parentId: string, parentPath: string) => {
        if (loadedFolders.has(parentId)) return;

        try {
            const childFolders = await getOneDriveFolders(parentId === 'root' ? undefined : parentId);
            const childNodes = convertToFolderNodes(childFolders, parentPath);

            setOneDriveFolders(prevFolders => {
                const updateChildren = (nodes: FolderNode[]): FolderNode[] => {
                    return nodes.map(node => {
                        if (node.id === parentId) {
                            return { ...node, children: childNodes };
                        } else if (node.children) {
                            return { ...node, children: updateChildren(node.children) };
                        }
                        return node;
                    });
                };
                return updateChildren(prevFolders);
            });

            setLoadedFolders(prev => {
                const next = new Set(prev);
                next.add(parentId);
                return next;
            });
        } catch (error) {
            console.error(`Failed to load child folders for ${parentId}:`, error);
        }
    };
    const buildFolderPaths = (nodes: FolderNode[], prefix = ""): Record<string, string> => {
        return nodes.reduce<Record<string, string>>((acc, node) => {
            const currentPath = prefix ? `${prefix}/${node.name}` : node.name;
            acc[node.id] = currentPath;
            if (node.children) {
                Object.assign(acc, buildFolderPaths(node.children, currentPath));
            }
            return acc;
        }, {});
    };

    const flattenFolders = (nodes: FolderNode[], depth = 0): { id: string; label: string }[] => {
        return nodes.flatMap((node) => {
            const prefix = depth === 0 ? "" : `${"— ".repeat(depth)}`;
            const current = [{ id: node.id, label: `${prefix}${node.name}` }];
            if (node.children) {
                return current.concat(flattenFolders(node.children, depth + 1));
            }
            return current;
        });
    };

    const folderPaths = useMemo(() => buildFolderPaths(oneDriveFolders), [oneDriveFolders]);
    const folderOptions = useMemo(() => flattenFolders(oneDriveFolders), [oneDriveFolders]);

    const workedOnProjects = useMemo(() => allProjects.filter((project) => project.lastPeriodWorked), [allProjects]);

    useEffect(() => {
        let isActive = true;
        const term = searchTerm.trim();

        if (!term) {
            setSearchResults([]);
            setIsSearchLoading(false);
            return () => {
                isActive = false;
            };
        }

        const timeoutId = window.setTimeout(async () => {
            try {
                setIsSearchLoading(true);
                const projects = await getProjects(term);
                if (isActive) {
                    setSearchResults(projects);
                }
            } catch (err) {
                if (isActive) {
                    console.error("Error searching projects:", err);
                    setSearchResults([]);
                }
            } finally {
                if (isActive) {
                    setIsSearchLoading(false);
                }
            }
        }, 250);

        return () => {
            isActive = false;
            window.clearTimeout(timeoutId);
        };
    }, [searchTerm]);

    const handleAddToFavorites = async (project: Project) => {
        if (addedProjects.has(project.id)) return;

        try {
            const folderItem: FolderItem = {
                id: null,
                name: `${project.projectNumber} - ${project.name}`,
                updatedAt: new Date().toISOString(),
                favoriteType: "project",
                hidden: false,
                projectNumber: project.projectNumber
            };

            await addUserFavorite(folderItem);
            onAddToFavorites(project.projectNumber);
            await refreshFavorites();
            setAddedProjects((prev) => {
                const next = new Set(prev);
                next.add(project.projectNumber);
                return next;
            });
        } catch (error) {
            console.error("Failed to add project to favorites:", error);
        }
    };

    const toggleFolder = (folderId: string) => {
        setExpandedFolders((prev) => {
            const next = new Set(prev);
            if (next.has(folderId)) {
                next.delete(folderId);
            } else {
                next.add(folderId);
            }
            return next;
        });
    };

    const renderFolderTree = (nodes: FolderNode[], depth = 0, forNewFolder = false): ReactNode => {
        const selectedFolder = forNewFolder ? newFolderParent : selectedOneDriveFolder;
        const setSelectedFolder = forNewFolder ? setNewFolderParent : setSelectedOneDriveFolder;

        return nodes.map((node) => {
            const hasChildren = !!node.children?.length;
            const isExpanded = expandedFolders.has(node.id);
            const isLoaded = loadedFolders.has(node.id);
            const depthClass = `onedrive-depth-${Math.min(depth, 4)}`;
            const ariaLabel = hasChildren ? (isExpanded ? "Collapse folder" : "Expand folder") : "Folder has no children";
            const isFavorite = currentFavorites?.filter((fav) => fav.favoriteType === "onedrive").some((fav) => fav.folderPath === node.id);

            return (
                <div key={node.id}>
                    <div className={`onedrive-folder-row ${depthClass} ${selectedFolder === node.id ? "active" : ""}`.trim()} onClick={() => { setSelectedFolder(node.id); isFavorite && oneDriveMode === 'existing' ? setOneDriveFolderState("existing") : setOneDriveFolderState("idle") }} title={isFavorite && oneDriveMode === 'existing' ? "Already in favorites" : "Select this folder"}>
                        <button
                            type="button"
                            className="onedrive-expand"
                            onClick={async (e) => {
                                e.stopPropagation();
                                if (!isLoaded && !isExpanded && oneDriveAvailable) {
                                    await loadChildFolders(node.id, node.path || node.name);
                                }
                                if (hasChildren || !isLoaded) toggleFolder(node.id);
                            }}
                            aria-label={ariaLabel}
                        >
                            {hasChildren || !isLoaded ? (isExpanded ? <MdExpandMore /> : <MdChevronRight />) : <span className="onedrive-expand-placeholder" />}
                        </button>
                        <span className={`onedrive-folder-icon ${isFavorite ? "favorite" : ""}`}>{hasChildren && isExpanded ? <MdFolderOpen /> : <MdFolder />}</span>
                        <span className="onedrive-folder-name">{node.name}</span>
                    </div>
                    {hasChildren && isExpanded && <div className="onedrive-children">{renderFolderTree(node.children!, depth + 1, forNewFolder)}</div>}
                </div>
            );
        });
    };

    const canArchiveToOneDrive = useMemo(() => {
        if (oneDriveMode === "existing") {
            return !!selectedOneDriveFolder;
        }
        return newFolderParent && newFolderName.trim().length > 0;
    }, [oneDriveMode, selectedOneDriveFolder, newFolderName]);

    const handlePrivateArchive = async () => {
        if (!oneDriveAvailable) {
            console.error('OneDrive is not available');
            return;
        }

        try {
            setOneDriveFolderState("creating");
            let targetFolderId: string;
            let destination: string;

            if (oneDriveMode === "existing") {
                targetFolderId = selectedOneDriveFolder!;
                destination = folderPaths[selectedOneDriveFolder ?? ""] ?? "OneDrive";
            } else {
                // Create new folder
                const parentId = newFolderParent === 'root' ? 'root' : newFolderParent;
                const newFolder = await createOneDriveFolder(parentId, newFolderName.trim());
                targetFolderId = newFolder.id;
                destination = `${folderPaths[newFolderParent] || "OneDrive"}/${newFolderName}`;

                // Update the folder tree to include the new folder
                const newFolderNode: FolderNode = {
                    id: newFolder.id,
                    name: newFolder.name,
                    path: destination,
                    children: []
                };

                setOneDriveFolders(prevFolders => {
                    const addToParent = (nodes: FolderNode[]): FolderNode[] => {
                        return nodes.map(node => {
                            if (node.id === newFolderParent) {
                                return {
                                    ...node,
                                    children: [...(node.children || []), newFolderNode]
                                };
                            } else if (node.children) {
                                return { ...node, children: addToParent(node.children) };
                            }
                            return node;
                        });
                    };
                    return addToParent(prevFolders);
                });
            }

            // Add OneDrive folder as a favorite
            const oneDriveFolderItem: FolderItem = {
                id: null,
                name: destination.includes('/') ? destination.split('/').pop()! : destination,
                updatedAt: new Date().toISOString(),
                favoriteType: "onedrive",
                hidden: false,
                folderPath: targetFolderId,
                keywords: []
            };

            await addUserFavorite(oneDriveFolderItem);
            onAddToFavorites(targetFolderId);
            await refreshFavorites();

            setOneDriveFolderState("created");

        } catch (error) {
            console.error('Failed to create OneDrive archive:', error);
            setOneDriveError('Failed to create folder in OneDrive');
        } finally {
            setTimeout(() => setOneDriveFolderState("idle"), 2000);
        }
    };

    return (
        <div className="add-to-favorites-container">
            <div className="favorites-header">
                <button className="back-button" onClick={navigateToFolders}>
                    ← Back
                </button>
                <h2>Add Favorites</h2>
            </div>

            <div className="favorites-mode-toggle">
                <button className={`mode-pill ${viewMode === "favorites" ? "active" : ""}`} onClick={() => setViewMode("favorites")}>
                    Project Folder
                </button>
                <button
                    className={`mode-pill ${viewMode === "onedrive" ? "active" : ""} ${!oneDriveAvailable ? "disabled" : ""}`}
                    onClick={() => oneDriveAvailable && setViewMode("onedrive")}
                    disabled={!oneDriveAvailable}
                    title={!oneDriveAvailable ? "OneDrive is not available. Please check your authentication." : "Archive emails to your personal OneDrive"}
                >
                    <MdCloudUpload /> Archive to OneDrive
                </button>
            </div>

            {viewMode === "favorites" ? (
                <>
                    <div className="favorites-search">
                        <div className="search-input-wrapper">
                            <input
                                type="text"
                                placeholder="Search projects by name, PM, or number..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="favorites-search-input"
                            />
                            {searchTerm && (
                                <button
                                    type="button"
                                    className="search-clear-button"
                                    aria-label="Clear search"
                                    title="Clear search"
                                    onClick={() => setSearchTerm("")}
                                >
                                    <MdClose />
                                </button>
                            )}
                        </div>
                    </div>

                    {isLoading && (
                        <div className="loading-state">
                            <p>Loading projects...</p>
                        </div>
                    )}

                    {error && !isLoading && (
                        <div className="error-state">
                            <p>{error}</p>
                        </div>
                    )}
                    <div className="favorites-content">
                        {!isLoading && !error && searchTerm.trim() && (
                            <div className="search-results-section">
                                <h3>Search Results {isSearchLoading ? "" : `(${searchResults.length})`}</h3>
                                <div className="projects-grid">
                                    {isSearchLoading ? (
                                        <div className="loading-state">
                                            <p>Searching projects...</p>
                                        </div>
                                    ) : (
                                        <>
                                            {searchResults.map((project) => (
                                                <div key={project.projectNumber} className="project-card">
                                                    <div className="project-info">
                                                        <div className="project-header">
                                                            <span className="project-number">#{project.projectNumber}</span>
                                                            <h4 className="project-name">{project.name}</h4>
                                                        </div>
                                                        {project.pmName && <p className="project-pm">PM: {project.pmName}</p>}
                                                    </div>
                                                    <button
                                                        className={`add-favorite-btn ${addedProjects.has(project.projectNumber) ? "added" : ""}`}
                                                        onClick={() => handleAddToFavorites(project)}
                                                        disabled={addedProjects.has(project.projectNumber)}
                                                    >
                                                        {addedProjects.has(project.projectNumber) ? "✓ Added to Favorites" : "+ Add to Favorites"}
                                                    </button>
                                                </div>
                                            ))}
                                            {searchResults.length === 0 && (
                                                <div className="no-results">
                                                    <p>No projects found matching "{searchTerm}"</p>
                                                </div>
                                            )}
                                        </>
                                    )}
                                </div>
                            </div>
                        )}

                        {!isLoading && !error && workedOnProjects.length > 0 && (
                            <div className="suggested-projects-section">
                                <h3>Suggested Projects</h3>
                                <p className="suggested-description">Projects from your recent work history</p>
                                <div className="projects-grid">
                                    {(searchResults.length > 0 ? workedOnProjects.slice(0, 2) : workedOnProjects.slice(0, 6)).map((project) => (
                                        <div key={project.projectNumber} className="project-card suggested">
                                            <div className="project-info">
                                                <div className="project-header">
                                                    <span className="project-number">#{project.projectNumber}</span>
                                                    <h4 className="project-name">{project.name}</h4>
                                                </div>
                                                {project.pmName && <p className="project-pm">PM: {project.pmName}</p>}
                                            </div>
                                            <button
                                                className={`add-favorite-btn ${addedProjects.has(project.projectNumber) ? "added" : ""}`}
                                                onClick={() => handleAddToFavorites(project)}
                                                disabled={addedProjects.has(project.projectNumber)}
                                            >
                                                {addedProjects.has(project.projectNumber) ? "✓ Added to Favorites" : "+ Add to Favorites"}
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                </>
            ) : (
                <div className="onedrive-archive-section">
                    <div className="onedrive-disclaimer">
                        <MdWarning className="onedrive-disclaimer-icon" />
                        <div>Private OneDrive archives are for personal reference only. Please do not store project-related emails here.</div>
                    </div>

                    {!oneDriveAvailable ? (
                        <div className="onedrive-error">
                            <MdWarning />
                            <p>OneDrive is not available. Please check your authentication and try again.</p>
                        </div>
                    ) : isOneDriveLoading ? (
                        <div className="onedrive-loading">
                            <p>Loading OneDrive folders...</p>
                        </div>
                    ) : oneDriveError ? (
                        <div className="onedrive-error">
                            <MdWarning />
                            <p>{oneDriveError}</p>
                        </div>
                    ) : (
                        <>
                            <div className="onedrive-mode-switch">
                                <button className={`onedrive-mode-btn ${oneDriveMode === "existing" ? "active" : ""}`} onClick={() => setOneDriveMode("existing")}>
                                    <MdFolder /> Save to existing folder
                                </button>
                                <button className={`onedrive-mode-btn ${oneDriveMode === "new" ? "active" : ""}`} onClick={() => setOneDriveMode("new")}>
                                    <MdCreateNewFolder /> Save to a new folder
                                </button>
                            </div>

                            {oneDriveMode === "existing" && (
                                <div className="onedrive-browser">
                                    <div className="onedrive-browser-header">Browse your OneDrive</div>
                                    <div className="onedrive-tree">{renderFolderTree(oneDriveFolders)}</div>
                                    <div className="onedrive-selection-summary">
                                        {selectedOneDriveFolder ? oneDriveFolderState === "existing" ? "Selected folder is already a favorite" : `Selected: ${folderPaths[selectedOneDriveFolder]}` : "Select a folder to continue"}
                                    </div>
                                </div>
                            )}

                            {oneDriveMode === "new" && (
                                <div className="onedrive-browser">
                                    <div className="onedrive-browser-header">Select parent folder</div>
                                    <div className="onedrive-tree">{renderFolderTree(oneDriveFolders, 0, true)}</div>
                                    <div className="onedrive-new-folder-input">
                                        <label className="onedrive-field">
                                            New folder name
                                            <input
                                                type="text"
                                                className="onedrive-input"
                                                placeholder="Enter folder name..."
                                                value={newFolderName}
                                                onChange={(e) => setNewFolderName(e.target.value)}
                                            />
                                        </label>
                                    </div>
                                    <div className="onedrive-selection-summary">
                                        {newFolderParent && newFolderName.trim()
                                            ? `Will create: ${folderPaths[newFolderParent]}/${newFolderName}`
                                            : newFolderParent
                                                ? "Enter a folder name to continue"
                                                : "Select a parent folder"}
                                    </div>
                                </div>
                            )}

                            <button
                                className={`onedrive-save ${oneDriveFolderState === "idle" ? "" : oneDriveFolderState}`}
                                disabled={!canArchiveToOneDrive || !oneDriveAvailable || oneDriveFolderState !== "idle"}
                                onClick={handlePrivateArchive}
                            >
                                {(() => {
                                    switch (oneDriveFolderState) {
                                        case "creating":
                                            return "Creating folder...";
                                        case "existing":
                                            return "Selected folder is already a favorite";
                                        case "created":
                                            return "Folder added to favorites!";
                                        default:
                                            return "Save private archive";
                                    }
                                })()}
                            </button>
                        </>
                    )}
                </div>
            )}
        </div>
    );
};

export default AddToFavorites;
